import { ContentfulStatusCode } from "hono/utils/http-status";
import { AccountType, AssetType, Prisma } from "@prisma/client-generated";
import { z } from "zod";
import {
  accountAndAssetSchemaCreate,
  accountAndAssetSchemaUpdate,
} from "./users/financial-disclosures/assets/schemas.js";
import {
  businessSchemaCreate,
  businessSchemaUpdate,
} from "./users/financial-disclosures/businesses/schemas.js";
import {
  debtSchemaCreate,
  debtSchemaUpdate,
} from "./users/financial-disclosures/debts/schemas.js";
import {
  incomeSchemaCreate,
  incomeSchemaUpdate,
} from "./users/financial-disclosures/incomes/schemas.js";
import {
  inheritanceAndTrustSchemaCreate,
  inheritanceAndTrustSchemaUpdate,
} from "./users/financial-disclosures/inheritances-trusts/schemas.js";
import {
  realEstateSchemaCreate,
  realEstateSchemaUpdate,
} from "./users/financial-disclosures/real-estates/schemas.js";
import {
  vehicleSchemaCreate,
  vehicleSchemaUpdate,
} from "./users/financial-disclosures/vehicles/schemas.js";
import {
  filePatchSchema,
  filePostSchema,
  uploadUrlSchema,
  documentFilePostSchema,
} from "./documents/files/schemas.js";
import { attorneyFilePostSchema } from "./users/attorney/files/schemas.js";
import { attorneyPatchSchema } from "./users/attorney/schemas.js";
import {
  createNotarizationSchema,
  notarizationPostSchema,
} from "./documents/notarizations/schemas.js";
export type {
  User,
  UserType,
  Document,
  State,
  Section,
  Clause,
  DocumentTemplate,
  Video,
  Answer,
  QuestionnaireSection,
  QuestionGroup,
  Question,
  QuestionRegionRestriction,
  QuestionOption,
  PurchaseItem,
  Attorney,
  LawFirm,
  AccountAndAsset,
  Business,
  Debt,
  Income,
  InheritanceAndTrust,
  RealEstate,
  LegacyJointProperty,
  Vehicle,
  File,
  DocumentStatus,
  AttorneyReview,
  QuestionConditionalVisibility,
  AttorneyRulesByQuestionOptionState,
  Event,
  Product,
  Cart,
  CartItem,
  Term,
  AttorneyService,
  AttorneyDisciplinaryRecord,
  ClauseTemplate,
  SectionTemplate,
} from "@prisma/client-generated";

// We can't directly export Enums from prisma as this will cause a type error
export {
  QuestionInputRestriction,
  Gender,
  AttorneyStage,
  AssetAccountType,
  AccountType,
  AssetType,
  BusinessType,
  IncomeType,
  InheritanceTrust, //todo: this should be "...Type" as its a category enum
  VehicleType,
  AttorneyReviewStatus,
  ProductType,
  PaymentStatus,
  AttorneyRulesByQuestionOptionStateType,
  CartStatus,
  PaymentMethod,
  EventStatus,
  PayoutStatus,
  BookingStatus,
  EditorClauseActions,
  EditorSectionActions,
  ListStyle,
  SectionTextStyle,
  ProductIcon,
  ProductCategory,
  AttorneySupport,
  SpousalSupportLaw,
} from "@prisma/client-generated";

export type FilePatchRequest = z.infer<typeof filePatchSchema>;
export type FilePostRequest = z.infer<typeof filePostSchema>;
export type AttorneyFilePostRequest = z.infer<typeof attorneyFilePostSchema>;
export type UploadUrlRequest = z.infer<typeof uploadUrlSchema>;
export type DocumentFilePostRequest = z.infer<typeof documentFilePostSchema>;

export type AttorneyWithUser = Prisma.AttorneyGetPayload<{
  include: {
    user: true;
  };
}>;

export type AttorneyWithStates = Prisma.AttorneyGetPayload<{
  include: {
    attorneyStates: {
      where: {
        deletedAt: null;
      };
    };
  };
}>;

export type DocumentWithRelations = Prisma.DocumentGetPayload<{
  include: {
    userA: true;
    userB: true;
    state: true;
    invitedUser: true;
    attorneyA: {
      include: {
        user: true;
      };
    };
    attorneyB: {
      include: {
        user: true;
      };
    };
    purchaseItems: {
      include: {
        product: true;
      };
    };
    template: {
      include: {
        documentTemplateStates: true;
      };
    };
    sections: {
      include: {
        clauses: true;
        clauseHistories: true;
      };
    };
    lock: true;
  };
}>;

export type SectionTemplateWithRelations = Prisma.SectionTemplateGetPayload<{
  include: {
    sectionTemplateQuestions: {
      include: {
        question: {
          include: {
            answers: {
              include: {
                answerQuestionOptions: true;
              };
            };
          };
        };
      };
    };
    clauseTemplates: {
      include: {
        clauseTemplateQuestions: {
          include: {
            question: {
              include: {
                answers: {
                  include: {
                    answerQuestionOptions: true;
                  };
                };
              };
            };
          };
        };
      };
    };
  };
}>;

export type SectionTemplateQuestionWithRelations =
  Prisma.SectionTemplateQuestionGetPayload<{
    include: {
      question: {
        include: {
          answers: {
            include: {
              answerQuestionOptions: true;
            };
          };
        };
      };
    };
  }>;

export type ClauseTemplateQuestionWithRelations =
  Prisma.ClauseTemplateQuestionGetPayload<{
    include: {
      question: {
        include: {
          answers: {
            include: {
              answerQuestionOptions: true;
            };
          };
        };
      };
    };
  }>;

export type ClauseTemplateWithRelations = Prisma.ClauseTemplateGetPayload<{
  include: {
    clauseTemplateQuestions: {
      include: {
        question: {
          include: {
            answers: {
              include: {
                answerQuestionOptions: true;
              };
            };
          };
        };
      };
    };
  };
}>;

export type AttorneyGetPayload = Prisma.AttorneyGetPayload<{
  include: {
    profilePhoto: true;
    malpracticeInsuranceFile: true;
    isLawFirmLead: true;
  };
}> & {
  profilePhotoUrl: string | null;
};

export type AttorneyUpdateInput = Prisma.AttorneyUpdateInput;

export type AttorneyPatchRequest = z.infer<typeof attorneyPatchSchema>;

export type LawFirmPostRequest = Prisma.LawFirmCreateInput;

export type AttorneyStateGetPayload = Prisma.AttorneyStateGetPayload<{
  include: {
    state: true;
  };
}>;

export type QuestionGroupWithQuestionsAndAnswers =
  Prisma.QuestionGroupGetPayload<{
    include: {
      questions: {
        include: {
          answers: {
            include: {
              answerQuestionOptions: true;
            };
          };
          questionOptions: {
            include: {
              questionConditionalVisibilities: true;
            };
          };
          parentConditionalVisibilities: true;
        };
      };
    };
  }>;

export type QuestionWithAnswers = Prisma.QuestionGetPayload<{
  include: {
    answers: {
      include: {
        answerQuestionOptions: true;
      };
    };
    parentConditionalVisibilities: true;
  };
}>;

export type QuestionnaireSectionWithStatus =
  Prisma.QuestionnaireSectionGetPayload<object> & {
    sectionStatus: QuestionnaireSectionStatus;
  };

// Define a reusable include type for questions
export interface QuestionIncludeType {
  questionStateRestrictions: true;
  questionOptions: {
    include: {
      restrictedQuestionOptions: true;
      requiredQuestionOptions: true;
      attorneyRulesByQuestionOptionState: true;
    };
  };
  videos: true;
  answers: {
    include: {
      answerQuestionOptions: true;
    };
  };
  parentConditionalVisibilities: true;
}

export type QuestionWithRelations = Prisma.QuestionGetPayload<{
  include: QuestionIncludeType;
}>;

export type QuestionGroupWithRelations = Prisma.QuestionGroupGetPayload<{
  include: {
    questions: {
      include: QuestionIncludeType;
    };
    videos: true;
  };
}>;

export type QuestionOptionWithRelations = Prisma.QuestionOptionGetPayload<{
  include: {
    restrictedQuestionOptions: true;
    requiredQuestionOptions: true;
  };
}>;

export type RestrictedQuestionOptionVisibility = Record<string, boolean>;

export interface QuestionnaireSectionsForNavigation {
  id: string;
  label: string;
  path: string;
  status: QuestionnaireSectionStatus;
  order: number;
  attorneyRequiredQuestionPresentSection: boolean;
}

export enum QuestionnaireSectionStatus {
  NOT_STARTED = "not_started",
  NOT_APPLICABLE = "not_applicable",
  COMPLETED = "completed",
  IN_PROGRESS = "in_progress",
}

export interface IError {
  message: string;
  status: ContentfulStatusCode;
}

export type E164Number = string;
export type Mutable<T> = {
  -readonly [key in keyof T]: T[key];
};

//we're narrowing accountAndAssetSchemaCreate to accountSchemaCreate here
export const accountSchemaCreate = accountAndAssetSchemaCreate
  .omit({
    assetType: true,
  })
  .extend({
    accountType: z.nativeEnum(AccountType),
  });
// and to a separate assetSchemaCreate
export const assetSchemaCreate = accountAndAssetSchemaCreate
  .omit({
    accountType: true,
  })
  .extend({
    assetType: z.nativeEnum(AssetType),
  });

export const accountSchemaUpdate = accountAndAssetSchemaUpdate.omit({
  assetType: true,
});

export const assetSchemaUpdate = accountAndAssetSchemaUpdate.omit({
  accountType: true,
});

export type AccountAndAssetCreateRequest = z.infer<
  typeof accountAndAssetSchemaCreate
>;
export type AccountAndAssetUpdateRequest = z.infer<
  typeof accountAndAssetSchemaUpdate
>;

export { businessSchemaCreate, businessSchemaUpdate };
export type BusinessCreateRequest = z.infer<typeof businessSchemaCreate>;
export type BusinessUpdateRequest = z.infer<typeof businessSchemaUpdate>;

export { debtSchemaCreate, debtSchemaUpdate };
export type DebtCreateRequest = z.infer<typeof debtSchemaCreate>;
export type DebtUpdateRequest = z.infer<typeof debtSchemaUpdate>;

export { incomeSchemaCreate, incomeSchemaUpdate };
export type IncomeCreateRequest = z.infer<typeof incomeSchemaCreate>;
export type IncomeUpdateRequest = z.infer<typeof incomeSchemaUpdate>;

export { inheritanceAndTrustSchemaCreate, inheritanceAndTrustSchemaUpdate };
export type InheritanceAndTrustCreateRequest = z.infer<
  typeof inheritanceAndTrustSchemaCreate
>;
export type InheritanceAndTrustUpdateRequest = z.infer<
  typeof inheritanceAndTrustSchemaUpdate
>;

export { realEstateSchemaCreate, realEstateSchemaUpdate };
export type RealEstateCreateRequest = z.infer<typeof realEstateSchemaCreate>;
export type RealEstateUpdateRequest = z.infer<typeof realEstateSchemaUpdate>;

export { vehicleSchemaCreate, vehicleSchemaUpdate };
export type VehicleCreateRequest = z.infer<typeof vehicleSchemaCreate>;
export type VehicleUpdateRequest = z.infer<typeof vehicleSchemaUpdate>;

export type Notarization = Prisma.NotarizationGetPayload<object>;
export type NotarizationPostRequest = z.infer<typeof notarizationPostSchema>;

export type Client = Prisma.UserGetPayload<{
  include: {
    purchaseItems: {
      include: {
        product: true;
        events: {
          where: {
            deletedAt: null;
          };
        };
        document: {
          include: {
            state: true;
            notarizations: {
              where: {
                notarizationCompletedAt: {
                  not: null;
                };
                deletedAt: null;
              };
              include: {
                notarizedFile: true;
              };
            };
          };
        };
      };
    };
    answers: true;
  };
}>;

export type UserWithPurchaseItems = Prisma.UserGetPayload<{
  include: {
    purchaseItems: {
      include: {
        product: true;
        attorney: true;
      };
    };
  };
}>;

export type NotarizationCreateProofRequest = z.infer<
  typeof createNotarizationSchema
>;

export type CartItemWithProduct = Prisma.CartItemGetPayload<{
  include: {
    product: true;
  };
}>;

export type {
  AnswerInput,
  QuestionOptionValue,
} from "./users/answers/schemas.js";

export enum EventFilter {
  ALL = "all",
  PAST = "past",
  FUTURE = "future",
}

export type StripeAccountStatus =
  | { unresolvedRequirements: string[] }
  | { error: string };

export interface StripeAccountLink {
  url: string;
  expiresAt: number;
}
